// Interfaces
// Constants
import { PlannerAsyncStoragePrefix } from "@ms/uno-constants/lib/local/PersistenceConstants";
import { Mode, IndexedDBErrors } from "./IndexedDBConstants";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
/** IndexedDB implementation */ export class IndexedDBStorage {
    /** ensures storage DB is initialized and open*/ async getDB() {
        if (this.markedForDelete) {
            throw new Error(`DBMarkedForDelete [Db=${this.name}]`);
        }
        if (this.database) {
            return this.database;
        }
        return this.initDB();
    }
    /** Method to initialize the storage
     * To be used to run the setup steps for the indexedDB storage
     */ async initDB() {
        if (this.dbOpenPromise) {
            return this.dbOpenPromise;
        }
        this.dbOpenPromise = new Promise((resolve, reject)=>{
            const request = indexedDB.open(this.name, this.version);
            let isUpgradeNeeded = false;
            request.onupgradeneeded = ()=>{
                isUpgradeNeeded = true;
                this.traceLogger.logTrace(0x1e28b0cb /* tag_4kldl */ , TraceLevel.Verbose, `initStorageDBOnupgradeneeded [DbVersion=${this.version}]`);
                const db = request.result;
                this.manageObjectStores(db);
            };
            request.onsuccess = ()=>{
                this.database = request.result;
                try {
                    // Only validate the schema if onupgradeneeded was not triggered
                    if (!isUpgradeNeeded) {
                        this.validateSchema(this.database);
                    }
                    this.traceLogger.logTrace(0x1e28b0ca /* tag_4kldk */ , TraceLevel.Verbose, `initStorageDBOpened [DbVersion=${this.version}]`);
                } catch (error) {
                    reject(error);
                    return;
                }
                this.database.onversionchange = ()=>{
                    this.traceLogger.logTrace(0x1e28b0c9 /* tag_4kldj */ , TraceLevel.Info, `initStorageDBOnversionchange [DbVersion=${this.version}]`);
                    this.database?.close();
                };
                resolve(this.database);
                this.dbOpenPromise = null;
            };
            request.onerror = (event)=>{
                reject(new Error(`initStorageDBOpenFailure [DbVersion=${this.version}][Error=${event.target?.error}]`));
                this.dbOpenPromise = null;
            };
            // The onblocked event is triggered when there is an attempt to open a database that is already open in another tab or process,
            // and the version change cannot proceed until the other connection is closed
            request.onblocked = ()=>{
                this.traceLogger.logTrace(0x1e24d254 /* tag_4jnju */ , TraceLevel.Warning, `Database upgrade blocked [DbVersion=${this.version}]`);
            };
        });
        return this.dbOpenPromise;
    }
    /** Method to manage object stores for the storage DB
     * creates any new object stores which do not exist already , as per schema specification on initial creation and db version upgrade
     * Also creates the specified indexes for the newly created object stores
     * Also manages deletes for the object stores which are not present in the current schema on db version upgrade
     * @param db database
     */ manageObjectStores(db) {
        // manage creation of object stores which do not exist already
        this.objectStoreSchema.forEach((store)=>{
            if (!db.objectStoreNames.contains(store.name)) {
                const objectStore = db.createObjectStore(store.name, {
                    keyPath: store.keyPath
                });
                if (objectStore) {
                    store.indexes.forEach((index)=>{
                        objectStore.createIndex(index.name, index.name, {
                            unique: index.isUnique,
                            multiEntry: index.isMultiEntry
                        });
                    });
                    this.traceLogger.logTrace(0x1e28b0c7 /* tag_4kldh */ , TraceLevel.Verbose, `initStorageObjectStoreCreated [DbVersion=${this.version}][store=${store.name}]`);
                }
            }
        });
        // Delete object stores not present in the current schema
        for (const storeName of db.objectStoreNames){
            if (!this.objectStoreSchema.some((store)=>store.name === storeName)) {
                db.deleteObjectStore(storeName);
            }
        }
    }
    /** Checks if indexedDB is supported on the browser */ static isIndexedDBSupported() {
        return "indexedDB" in window;
    }
    /** Validates that the schema of the database matches TStorageSchema */ validateSchema(db) {
        try {
            // Iterate over the expected schema
            this.objectStoreSchema.forEach((storeSchema)=>{
                // Check if the object store exists
                if (!db.objectStoreNames.contains(storeSchema.name)) {
                    throw new Error(`Missing object store [store=${storeSchema.name}]`);
                }
                // Validate indexes for the object store
                const transaction = db.transaction(storeSchema.name, Mode.ReadOnly);
                const store = transaction.objectStore(storeSchema.name);
                storeSchema.indexes.forEach((expectedIndex)=>{
                    try {
                        const index = store.index(expectedIndex.name);
                        if (index.unique !== expectedIndex.isUnique || index.multiEntry !== expectedIndex.isMultiEntry) {
                            throw new Error(`Index mismatch [store=${storeSchema.name}][index=${expectedIndex.name}]`);
                        }
                    } catch (error) {
                        throw new Error(`Missing index [store=${storeSchema.name}][index=${expectedIndex.name}][originalError=${error.message}]`);
                    }
                });
            });
            // Check for extra object stores not in the schema
            for (const storeName of db.objectStoreNames){
                if (!this.objectStoreSchema.some((store)=>store.name === storeName)) {
                    throw new Error(`Extra object store found [store=${storeName}]`);
                }
            }
        } catch (error) {
            // Close the database if schema validation fails
            this.database?.close();
            this.database = null;
            throw new Error(`SchemaValidationError [DbVersion=${this.version}][Error=${error.message}]`);
        }
    }
    async read(objectStore, key) {
        const db = await this.getDB();
        return new Promise((resolve, reject)=>{
            try {
                const transaction = db.transaction(objectStore, Mode.ReadOnly);
                const store = transaction.objectStore(objectStore);
                const request = store.get(key);
                request.onsuccess = ()=>{
                    const result = request.result;
                    resolve(result ? result : undefined);
                };
                request.onerror = (event)=>{
                    reject(new Error(`DBReadFailed [DbVersion=${this.version}][store=${objectStore}][Error=${event.target?.error}]`));
                };
            } catch (error) {
                reject(new Error(`ReadFailed [DbVersion=${this.version}][objectStore=${objectStore}][Error=${error}]`));
            }
        });
    }
    async readAll(objectStore) {
        const db = await this.getDB();
        return new Promise((resolve, reject)=>{
            try {
                const transaction = db.transaction(objectStore, Mode.ReadOnly);
                const store = transaction.objectStore(objectStore);
                const keyPath = store.keyPath;
                const request = store.getAll();
                request.onsuccess = ()=>{
                    const formattedResults = {};
                    request.result.forEach((item)=>{
                        formattedResults[item[keyPath]] = item;
                    });
                    resolve(formattedResults);
                };
                request.onerror = (event)=>{
                    reject(new Error(`DBReadAllFailed [DbVersion=${this.version}][store=${objectStore}][Error=${event.target?.error}]`));
                };
            } catch (error) {
                reject(new Error(`ReadAllFailed [DbVersion=${this.version}][objectStore=${objectStore}][Error=${error}]`));
            }
        });
    }
    async readByIndex(objectStore, indexName, indexValue) {
        const db = await this.getDB();
        return new Promise((resolve, reject)=>{
            try {
                const transaction = db.transaction(objectStore, Mode.ReadOnly);
                const store = transaction.objectStore(objectStore);
                const keyPath = store.keyPath;
                const index = store.index(indexName);
                const request = index.getAll(indexValue);
                request.onsuccess = ()=>{
                    const formattedResults = {};
                    request.result.forEach((item)=>{
                        formattedResults[item[keyPath]] = item;
                    });
                    resolve(formattedResults);
                };
                request.onerror = (event)=>{
                    reject(new Error(`DBReadByIndexFailed [DbVersion=${this.version}][store=${objectStore}][index=${indexName}][Error=${event.target?.error}]`));
                };
            } catch (error) {
                reject(new Error(`ReadByIndexFailed [DbVersion=${this.version}][objectStore=${objectStore}][Error=${error}]`));
            }
        });
    }
    async batchRead(operations) {
        const db = await this.getDB();
        return new Promise((resolve, reject)=>{
            try {
                const storeNames = Object.keys(operations);
                if (storeNames.length === 0) {
                    resolve({});
                    return;
                }
                const transaction = db.transaction(storeNames, Mode.ReadOnly);
                const results = {};
                storeNames.forEach((storeName)=>{
                    const store = transaction.objectStore(storeName);
                    results[storeName] = {};
                    operations[storeName].forEach((key)=>{
                        const request = store.get(key);
                        request.onsuccess = ()=>{
                            if (request.result) {
                                results[storeName][key] = request.result;
                            }
                        };
                        request.onerror = (event)=>{
                            const error = event.target?.error;
                            this.traceLogger.logTrace(0x1e21780a /* tag_4ix6k */ , TraceLevel.Error, `DBBatchReadFailed [DbVersion=${this.version}][store=${storeName}][Error=${error}]`);
                        };
                    });
                });
                transaction.oncomplete = ()=>{
                    resolve(results);
                };
                transaction.onabort = ()=>{
                    reject(new Error(`batchReadTransactionAborted [DbVersion=${this.version}]`));
                };
                transaction.onerror = (event)=>{
                    reject(new Error(`BatchReadFailed [DbVersion=${this.version}][Error=${event.target?.error}]`));
                };
            } catch (error) {
                reject(new Error(`BatchReadFailed [DbVersion=${this.version}][Error=${error}]`));
            }
        });
    }
    async write(objectStore, items, operationRetryAttempts) {
        const operationRecord = {};
        operationRecord[objectStore] = {
            itemsToWrite: items,
            keysToPurge: []
        };
        return this.performBatchOperation(operationRecord, operationRetryAttempts);
    }
    async purge(objectStore, keys, operationRetryAttempts) {
        const operationRecord = {};
        operationRecord[objectStore] = {
            itemsToWrite: [],
            keysToPurge: keys
        };
        return this.performBatchOperation(operationRecord, operationRetryAttempts);
    }
    async performBatchOperation(operations, operationRetryAttempts) {
        const db = await this.getDB();
        return new Promise((resolve, reject)=>{
            let attempt = 0;
            let lastError = null;
            const executeTransaction = ()=>{
                lastError = null;
                attempt++;
                let isTransactionAborted = false;
                try {
                    const storeNames = Object.keys(operations);
                    const transaction = db.transaction(storeNames, Mode.ReadWrite);
                    for (const storeName of storeNames){
                        if (isTransactionAborted) {
                            break;
                        }
                        const store = transaction.objectStore(storeName);
                        const { itemsToWrite, keysToPurge } = operations[storeName];
                        for (const item of itemsToWrite){
                            if (isTransactionAborted) {
                                break;
                            }
                            try {
                                const request = store.put(item);
                                request.onerror = (event)=>{
                                    if (!isTransactionAborted) {
                                        const error = event.target?.error;
                                        lastError = error || new DOMException("Unknown error during write", IndexedDBErrors.UnknownError);
                                        isTransactionAborted = true;
                                        transaction.abort();
                                    }
                                };
                            } catch (error) {
                                if (!isTransactionAborted) {
                                    lastError = error;
                                    isTransactionAborted = true;
                                    transaction.abort();
                                }
                            }
                        }
                        for (const key of keysToPurge){
                            if (isTransactionAborted) {
                                break;
                            }
                            try {
                                const request = store.delete(key);
                                request.onerror = (event)=>{
                                    if (!isTransactionAborted) {
                                        const error = event.target?.error;
                                        if (error?.name === IndexedDBErrors.NotFoundError) {
                                            this.traceLogger.logTrace(0x1e217806 /* tag_4ix6g */ , TraceLevel.Info, `performBatchOperationDeleteKeyNotFound [DbVersion=${this.version}][store=${storeName}]`);
                                        } else {
                                            lastError = error || new DOMException("Unknown error during delete", IndexedDBErrors.UnknownError);
                                            isTransactionAborted = true;
                                            transaction.abort();
                                        }
                                    }
                                };
                            } catch (error) {
                                if (!isTransactionAborted) {
                                    lastError = error;
                                    isTransactionAborted = true;
                                    transaction.abort();
                                }
                            }
                        }
                        transaction.oncomplete = ()=>{
                            resolve();
                        };
                        transaction.onabort = ()=>{
                            this.traceLogger.logTrace(0x1e217803 /* tag_4ix6d */ , TraceLevel.Warning, `performBatchOperationTransactionAborted [DbVersion=${this.version}][Attempt=${attempt}][Error=${lastError?.message}]`);
                            handleRetry(lastError || new DOMException("Transaction aborted", IndexedDBErrors.AbortError));
                        };
                        transaction.onerror = (event)=>{
                            const error = event.target?.error;
                            handleRetry(new DOMException("Transaction failed", error?.name || IndexedDBErrors.UnknownError));
                        };
                    }
                } catch (error) {
                    handleRetry(new DOMException(`performBatchOperationsFailed [DbVersion=${this.version}]`, error));
                }
            };
            const handleRetry = (error)=>{
                if (attempt <= operationRetryAttempts && error && (error.name === IndexedDBErrors.AbortError || error.name === IndexedDBErrors.ConstraintError || error.name === IndexedDBErrors.UnknownError)) {
                    executeTransaction(); // Retry the transaction
                } else {
                    this.traceLogger.logTrace(0x1e217801 /* tag_4ix6b */ , TraceLevel.Error, `performBatchOperationFailed [DbVersion=${this.version}][Error=${error.name}]`);
                    reject(error); // Reject for non-retriable errors or after max retries
                }
            };
            // Start the first attempt
            executeTransaction();
        });
    }
    async clearObjectStore(objectStore) {
        const db = await this.getDB();
        return new Promise((resolve, reject)=>{
            try {
                const transaction = db.transaction(objectStore, Mode.ReadWrite);
                const store = transaction.objectStore(objectStore);
                const request = store.clear();
                request.onsuccess = ()=>{
                    this.traceLogger.logTrace(0x1e28a023 /* tag_4kka9 */ , TraceLevel.Info, `DBObjectStoreCleared [DbVersion=${this.version}][store=${objectStore}]`);
                    resolve();
                };
                request.onerror = (event)=>{
                    reject(new Error(`DBObjectStoreClearFailed [DbVersion=${this.version}][store=${objectStore}][Error=${event.target?.error}]`));
                };
            } catch (error) {
                reject(new Error(`DBObjectStoreClearError [DbVersion=${this.version}][store=${objectStore}][Error=${error}]`));
            }
        });
    }
    async deleteCurrentDatabase() {
        // Block further opens before tearing down.
        this.markedForDelete = true;
        // Wait for any in-flight open to finish, then close our connection so the delete
        // isn't blocked on it. Without the wait, a late onsuccess could reopen mid-delete.
        if (this.dbOpenPromise) {
            try {
                await this.dbOpenPromise;
            } catch  {
            // Open failed; nothing to close.
            }
        }
        if (this.database) {
            try {
                this.database.close();
            } catch (error) {
                // Connection didn't close, so the delete would block anyway — abort and restore the instance.
                this.traceLogger.logTrace(0x1d891556 /* tag_38rvw */ , TraceLevel.Error, `deleteCurrentDatabase - close() failed, aborting delete [Db=${this.name}][Error=${error}]`);
                this.database = null;
                this.dbOpenPromise = null;
                this.markedForDelete = false;
                throw new Error(`DBCloseFailed [Db=${this.name}][Error=${error}]`);
            }
            this.database = null;
        }
        this.dbOpenPromise = null;
        return this.deleteDatabase(this.name);
    }
    async deleteAllStaleDatabases() {
        try {
            const allStaleDatabaseNames = (await indexedDB.databases()).map((db)=>db.name).filter((dbName)=>dbName != null) // Filter out undefined names
            .filter((dbName)=>dbName !== this.name && dbName.startsWith(PlannerAsyncStoragePrefix)); // Filter current db name and the names starting with PlannerAsyncStoragePrefix
            const deleteDBPromises = allStaleDatabaseNames.map((dbName)=>this.deleteDatabase(dbName));
            await Promise.all(deleteDBPromises);
            this.traceLogger.logTrace(0x1e240059 /* tag_4jabz */ , TraceLevel.Info, `Cleared all stale databases [Count=${allStaleDatabaseNames.length}]`);
        } catch (error) {
            this.traceLogger.logTrace(0x1e240058 /* tag_4jaby */ , TraceLevel.Error, `Failed to clear all stale databases [Error=${error}]`);
        }
    }
    /**
     * Deletes the specified database
     * @param dbName name of the database to be deleted
     */ async deleteDatabase(dbName) {
        return new Promise((resolve, reject)=>{
            const request = indexedDB.deleteDatabase(dbName);
            request.onsuccess = ()=>{
                // Only null our live connection when we initiated the delete.
                if (dbName === this.name && this.markedForDelete) {
                    this.database = null;
                }
                this.traceLogger.logTrace(0x1e28a01f /* tag_4kka5 */ , TraceLevel.Info, `DBDeleted [Db=${dbName}]`);
                resolve();
            };
            request.onerror = (event)=>{
                reject(new Error(`DBClearFailed [DbVersion=${this.version}][Error=${event.target?.error}]`));
            };
            request.onblocked = ()=>{
                // Triggered if the deletion is blocked, for eg, if there are open connections to the database
                reject(new Error(`DBDeletionBlocked [Db=${dbName}]`));
            };
        });
    }
    constructor(userId, databaseNamePrefix, version, objectStoreSchema, traceLogger){
        /** indexedDB instance */ this.database = null;
        /** IDBDatabase open promise */ this.dbOpenPromise = null;
        /** Once true, getDB() rejects — prevents a concurrent open from racing the delete. */ this.markedForDelete = false;
        this.name = `${databaseNamePrefix}_${userId}`;
        this.version = version;
        this.objectStoreSchema = objectStoreSchema;
        this.traceLogger = traceLogger;
        // Starting the initialization of the storage in the constructor itself to minimize the delay before DB is ready for operations
        // Promise is handled separately below to ensure the initialization completes before accessing the DB
        this.initDB().catch((error)=>{
            this.traceLogger.logTrace(0x1e241785 /* tag_4jb4f */ , TraceLevel.Error, `initDB [Error=${error}]`);
        });
    }
}
